import { Button } from '@/components/ui-kit/button';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowLeft, Wand2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ApplyStandardsBanner from './ApplyStandardsBanner';
import { ScreenWithFields, useDynamicForm } from './DynamicFormContext';
import DynamicField, { FieldConfig } from './field-renderers';
import { DynamicFieldType } from './hooks/useDynamicFields';

interface DynamicScreenContentProps {
  currentScreen: ScreenWithFields;
  dynamicFieldsLoading: boolean;
  dynamicFieldsError: string | null;
  dynamicFieldsCache: Record<string, DynamicFieldType[]>;
  jurisdictionName: string | null;
  appliedStandard: boolean;
  applyingStandard: boolean;
  onApplyDynamicStandards: (fields: DynamicFieldType[]) => void;
  setFieldValue: (name: string, value: unknown) => void;
  onRetry: () => void;
  onSkip: () => void;
}

export default function DynamicScreenContent({
  currentScreen,
  dynamicFieldsLoading,
  dynamicFieldsError,
  dynamicFieldsCache,
  jurisdictionName,
  appliedStandard,
  applyingStandard,
  onApplyDynamicStandards,
  setFieldValue,
  onRetry,
  onSkip,
}: DynamicScreenContentProps) {
  const { t } = useTranslation();
  const { formData, errors, enrichmentContext } = useDynamicForm();

  const fields = dynamicFieldsCache[currentScreen.id];

  if (dynamicFieldsLoading) {
    return <LoadingState />;
  }

  if (dynamicFieldsError) {
    return <ErrorState error={dynamicFieldsError} onRetry={onRetry} onSkip={onSkip} />;
  }

  if (fields) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* AI Generated Badge */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--brand-primary))] bg-[hsl(var(--brand-primary))]/5 px-3 py-2 rounded-lg border border-[hsl(var(--brand-primary))]/20">
            <Wand2 className="h-3.5 w-3.5" />
            <span>{t('EMPLOYMENTAGREEMENT_SMARTFLOW_DYNAMIC_AIBADGE')}</span>
          </div>
        </div>

        <ApplyStandardsBanner
          variant="dynamic"
          currentScreen={currentScreen}
          fields={fields}
          jurisdictionName={jurisdictionName}
          appliedStandard={appliedStandard}
          applyingStandard={applyingStandard}
          onApply={() => onApplyDynamicStandards(fields)}
        />

        {fields.map((field) => (
          <motion.div
            key={field.id}
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0 },
            }}
            initial="hidden"
            animate="show"
            exit={{ opacity: 0, y: -20 }}
            layout
            data-field-error={errors[field.name] ? 'true' : undefined}
          >
            <DynamicField
              field={
                {
                  ...field,
                  type: field.type as string,
                  aiSuggestionEnabled: !!field.standardValue,
                  aiSuggestionKey: field.standardValue ? `_dynamic_${field.name}` : undefined,
                } as FieldConfig
              }
              value={formData[field.name]}
              onChange={setFieldValue}
              error={errors[field.name]}
              enrichmentContext={{
                ...enrichmentContext,
                ...(field.standardValue ? { [`_dynamic_${field.name}`]: field.standardValue } : {}),
              }}
              formData={formData}
            />
          </motion.div>
        ))}
      </motion.div>
    );
  }

  // Waiting state while prefetch is in progress
  return <WaitingState />;
}

function LoadingState() {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center py-16 space-y-6"
    >
      <div className="relative">
        <motion.div
          className="h-20 w-20 rounded-full border-4 border-[hsl(var(--brand-primary))]/20"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute inset-0 h-20 w-20 rounded-full border-4 border-transparent border-t-[hsl(var(--brand-primary))]"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute inset-0 m-auto flex items-center justify-center"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Wand2 className="h-8 w-8 text-[hsl(var(--brand-primary))]" />
        </motion.div>
      </div>
      <div className="text-center space-y-2">
        <p className="text-lg font-semibold text-[hsl(var(--fg))]">
          {t('EMPLOYMENTAGREEMENT_SMARTFLOW_DYNAMIC_LOADINGTITLE')}
        </p>
        <p className="text-sm text-[hsl(var(--brand-muted))] max-w-sm">
          {t('EMPLOYMENTAGREEMENT_SMARTFLOW_DYNAMIC_LOADINGDESCRIPTION')}
        </p>
      </div>
    </motion.div>
  );
}

function ErrorState({
  error,
  onRetry,
  onSkip,
}: {
  error: string;
  onRetry: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center py-12 space-y-6"
    >
      <div className="h-20 w-20 rounded-2xl bg-red-50 flex items-center justify-center">
        <AlertTriangle className="h-10 w-10 text-red-500" />
      </div>
      <div className="text-center space-y-2">
        <p className="text-lg font-semibold text-red-600">
          {t('EMPLOYMENTAGREEMENT_SMARTFLOW_DYNAMIC_ERRORTITLE')}
        </p>
        <p className="text-sm text-[hsl(var(--brand-muted))] max-w-sm">{error}</p>
      </div>
      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={onRetry} className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_TRYAGAIN')}
        </Button>
        <Button variant="ghost" onClick={onSkip} className="text-globe-grey">
          {t('EMPLOYMENTAGREEMENT_SMARTFLOW_NAVIGATION_SKIPSTEP')}
        </Button>
      </div>
    </motion.div>
  );
}

function WaitingState() {
  const { t } = useTranslation();
  return (
    <div className="py-8 text-center">
      <div className="flex flex-col items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-selise-blue animate-pulse" />
          <div className="h-2 w-2 rounded-full bg-selise-blue animate-pulse [animation-delay:0.2s]" />
          <div className="h-2 w-2 rounded-full bg-selise-blue animate-pulse [animation-delay:0.4s]" />
        </div>
        <p className="text-globe-grey">{t('EMPLOYMENTAGREEMENT_SMARTFLOW_DYNAMIC_WAITINGTITLE')}</p>
        <p className="text-sm text-globe-grey opacity-70">
          {t('EMPLOYMENTAGREEMENT_SMARTFLOW_DYNAMIC_WAITINGDESCRIPTION')}
        </p>
      </div>
    </div>
  );
}
